import { captureException, init as SentryInit, makeFetchTransport, makeMultiplexedTransport } from '@sentry/browser';
import { navStateStore } from '../state/nav-state-store';
import { Envelope, EnvelopeItemType, ErrorEvent, Event, EventHint } from '@sentry/types';
import featureToggles from './feature-toggles';
import windowService from './window-service';
import { VERSION } from '../utility/constants';
import { BrowserOptions } from '@sentry/browser/types/client';

type Origin = {
  dsn: string;
  release: string;
  originId: string;
};

type AppIdentifier = {
  dsn: string;
  environment: string;
  release: string;
};

type DsnFromAppsParams = {
  getEvent(types?: EnvelopeItemType[]): Event | undefined;
  environment: string;
  origins: Origin[];
  envelope?: Envelope;
};

export function dsnFromApps({ getEvent, environment, origins }: DsnFromAppsParams) {
  const event = getEvent();
  const additionalHandlers = Object.fromEntries(
    origins.map(({ originId, dsn, release }) => [originId, [{ dsn, environment, release }]])
  );

  const handlers: Record<string, AppIdentifier[]> = {
    default: [],
    PHN_HEADER: [{ ...phnHeaderSentryIdentifier(), environment }],
    ...additionalHandlers
  };

  const originId = (event?.tags?.originId as string) || 'default';

  return handlers[originId] || [];
}

export function initMonitoring({
  origins,
  environment,
  sentryOptions = {}
}: {
  origins: Origin[];
  environment: string;
  sentryOptions: BrowserOptions;
}) {
  SentryInit({
    transport: makeMultiplexedTransport(makeFetchTransport, (match) => dsnFromApps({ ...match, environment, origins })),
    ...sentryOptions
  });
}

export function initNaviMonitoring(): void {
  if (featureToggles.isSentryEnabled()) {
    if (typeof window !== 'undefined' && !(window.SENTRY || window.__SENTRY__)) {
      SentryInit({
        dsn: 'SENTRY_DSN',
        environment: navStateStore.state.env || undefined,
        release: VERSION,
        beforeSend: (event: ErrorEvent, _: EventHint): Event | null => {
          if (event.tags?.originId === 'PHN_HEADER') return event;
          return null;
        }
      });
    }
  }
}

export function phnHeaderSentryIdentifier(): { dsn: string; release: string } {
  return {
    dsn: 'SENTRY_DSN',
    release: VERSION
  };
}

export function captureNaviException(err: Error, extras: { breakpoint: number; locale: string | null }): void {
  captureException(err, {
    tags: {
      ...extras,
      version: VERSION,
      env: navStateStore.state.env || undefined,
      originId: 'PHN_HEADER',
      hostname: windowService.location.hostname,
      pathname: windowService.location.pathname
    }
  });
}
